import { addMonths, format, parseISO } from "date-fns";
import { BillingInterval, DiscountType, SubscriptionStatus } from "../../Data/SD";
import { AllUsers } from "../../interfaces/identity/calendar/Events";

export function toShortAmPm(time: string) {
    if (!time) return '';
    let [h, m] = time.split(':').map(Number);
    let ampm = h >= 12 ? 'pm' : 'am';
    let hour = h % 12 === 0 ? 12 : h % 12;
    // Only show minutes if not 00
    return m === 0 ? `${hour}${ampm}` : `${hour}:${m < 10 ? '0' : ''}${m}${ampm}`;
}


// Helper to convert "10:07am" or "11:07pm" to "HH:mm" 24-hour format
export function to24Hour(time: string): string {
    if (!time) return "";
    // If already in 24-hour format (HH:mm)
    if (/^\d{2}:\d{2}$/.test(time)) return time;
    // Match "hh:mmam/pm" or "hham/pm"
    const match = time.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/i);
    if (!match) return "";
    let [, hour, minute, period] = match;
    let h = parseInt(hour, 10);
    let m = minute ? parseInt(minute, 10) : 0;
    if (period.toLowerCase() === "pm" && h !== 12) h += 12;
    if (period.toLowerCase() === "am" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

export const getSortIcon = (active: boolean, direction: string) => {
    if (!active) return "⇅";
    return direction === "asc" ? "▲" : "▼";
};


export const GetUserWhoNotHaveEmails = (
    allusers: AllUsers[],
    selectededUserIds: string[]
): string[] => {
    const selectedUserIdSet = new Set(selectededUserIds);

    return allusers
        .filter(
            user =>
                selectedUserIdSet.has(user.userId) &&
                (!user.email || user.email.trim() === "")
        )
        .map(user => user.userId);
};

export const SubscriptionStatusLabel: Record<SubscriptionStatus, string> = {
    [SubscriptionStatus.TRIAL_ACTIVE]: "Trial Active",
    [SubscriptionStatus.ACTIVE]: "Active",
    [SubscriptionStatus.READ_ONLY_TRIAL_EXPIRED]: "Trial Expired (Read Only)",
    [SubscriptionStatus.READ_ONLY_DELINQUENT]: "Delinquent (Read Only)",
    [SubscriptionStatus.SUSPENDED]: "Suspended",
};

export const centToUSD = (cents: number | undefined) => {
    if (!cents) return "$ 0.00";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(cents / 100);
}

export const usdToCent = (usd: number) => Math.round(usd * 100);

export const getNextBillingDate = (
    startDate: string | Date,
    interval: BillingInterval
) => {
    let next = startDate instanceof Date ? new Date(startDate) : parseISO(startDate);
    if (Number.isNaN(+next)) return "Invalid date";
    const now = new Date();
    const months = interval === BillingInterval.MONTHLY ? 1 : interval === BillingInterval.QUATERLY ? 3 : interval === BillingInterval.ANNUAL ? 12 : 0;
    if (!months) return format(next, "MMM d, yyyy");
    while (next <= now) next = addMonths(next, months);
    return format(next, "MMM d, yyyy");
};


export function calculateDiscount(
    subtotal: number,
    discountType: DiscountType,
    discountValue: number
): number {
    switch (discountType) {
        case DiscountType.NONE:
            return 0;

        case DiscountType.PERCENT:
            return Math.floor(subtotal * (discountValue / 100));

        case DiscountType.FIXED:
            return usdToCent(discountValue);

        default:
            throw new Error("Invalid discount type");
    }
}